import React, { useEffect, useState } from 'react';
import API from '../services/api';

// Review popup for an Acuity new-client request: shows what Acuity collected
// and records the therapist's decision (accept / request info / reject).
// The banner persists until a decision is made ("Not now" just closes).
export default function NewClientReviewModal({ prompt, onClose, onDecided, onCreateClient }) {
  const [view, setView] = useState('main'); // main | info | reject | done
  const [prefill, setPrefill] = useState(null);
  const [loadingPrefill, setLoadingPrefill] = useState(true);
  const [infoMode, setInfoMode] = useState('info_admin'); // info_admin | info_self
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [warnings, setWarnings] = useState([]);
  const [doneDecision, setDoneDecision] = useState(null);

  useEffect(() => {
    let active = true;
    API.get(`/therapist/new-client-prompts/${prompt.acuityId}/prefill`)
      .then((res) => { if (active) setPrefill(res.data || null); })
      .catch(() => { /* non-fatal: basic prompt info still shows */ })
      .finally(() => { if (active) setLoadingPrefill(false); });
    return () => { active = false; };
  }, [prompt.acuityId]);

  const decide = async (decision, noteValue) => {
    setSubmitting(true);
    setError('');
    try {
      const res = await API.post(`/therapist/new-client-prompts/${prompt.acuityId}/decide`, {
        decision,
        note: noteValue || undefined,
      });
      setWarnings((res.data && res.data.warnings) || []);
      setDoneDecision(decision);
      setView('done');
      onDecided(prompt.acuityId);
    } catch (err) {
      if (err.response && err.response.status === 409) {
        setError('This request was already decided (maybe in another tab).');
        onDecided(prompt.acuityId);
      } else {
        setError((err.response && err.response.data && err.response.data.message) || 'Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const fullName = `${prompt.firstName || ''} ${prompt.lastName || ''}`.trim() || 'New client';
  const apptStr = prompt.apptDatetime
    ? new Date(prompt.apptDatetime).toLocaleString('en-US', { dateStyle: 'full', timeStyle: 'short' })
    : null;

  // Prefill fields worth showing, in a stable order; `notes` last since it can be long.
  const detailEntries = prefill
    ? Object.entries(prefill).filter(([k, v]) => v && k !== 'firstName' && k !== 'lastName')
    : [];

  return (
    <div style={overlayStyle} onClick={submitting ? undefined : onClose}>
      <div style={boxStyle} onClick={(e) => e.stopPropagation()}>
        <div style={headerStyle}>
          <h3 style={{ margin: 0 }}>New client request — {fullName}</h3>
          <button onClick={onClose} style={closeBtnStyle} aria-label="Close" disabled={submitting}>&times;</button>
        </div>

        {view === 'main' && (
          <>
            {apptStr && <div style={{ marginBottom: 12, fontWeight: 600 }}>📅 {apptStr}</div>}
            <div style={detailsBoxStyle}>
              {loadingPrefill ? (
                <div style={{ color: 'var(--text-muted)' }}>Loading intake details…</div>
              ) : detailEntries.length === 0 ? (
                <div style={{ color: 'var(--text-muted)' }}>No additional intake details from Acuity.</div>
              ) : (
                detailEntries.map(([k, v]) => (
                  <div key={k} style={{ marginBottom: 6 }}>
                    <span style={{ fontWeight: 600 }}>{labelize(k)}: </span>
                    <span style={{ whiteSpace: 'pre-wrap' }}>{String(v)}</span>
                  </div>
                ))
              )}
            </div>
            {error && <div style={errorStyle}>{error}</div>}
            <div style={actionRowStyle}>
              <button style={btn('var(--success-color)')} disabled={submitting} onClick={() => decide('accepted')}>
                ✓ Accept
              </button>
              <button style={btn('var(--accent-color)')} disabled={submitting} onClick={() => { setNote(''); setView('info'); }}>
                ？ Request more info
              </button>
              <button style={btn('var(--error-color)')} disabled={submitting} onClick={() => { setNote(''); setView('reject'); }}>
                ✕ Reject
              </button>
              <button style={btnGhost} disabled={submitting} onClick={onClose}>
                Not now
              </button>
            </div>
          </>
        )}

        {view === 'info' && (
          <>
            <div style={{ marginBottom: 10, fontWeight: 600 }}>How should the follow-up happen?</div>
            <label style={radioRowStyle}>
              <input type="radio" name="infoMode" checked={infoMode === 'info_admin'} onChange={() => setInfoMode('info_admin')} />
              <span>Ask the office to follow up with {fullName}</span>
            </label>
            <label style={radioRowStyle}>
              <input type="radio" name="infoMode" checked={infoMode === 'info_self'} onChange={() => setInfoMode('info_self')} />
              <span>I'll reach out myself</span>
            </label>
            {infoMode === 'info_admin' && (
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="What should the office ask or find out?"
                style={textareaStyle}
              />
            )}
            {error && <div style={errorStyle}>{error}</div>}
            <div style={actionRowStyle}>
              <button
                style={btn('var(--primary-color)')}
                disabled={submitting || (infoMode === 'info_admin' && !note.trim())}
                onClick={() => decide(infoMode, infoMode === 'info_admin' ? note.trim() : undefined)}
              >
                {submitting ? 'Saving…' : 'Save'}
              </button>
              <button style={btnGhost} disabled={submitting} onClick={() => setView('main')}>Back</button>
            </div>
          </>
        )}

        {view === 'reject' && (
          <>
            <div style={{ marginBottom: 10, fontWeight: 600 }}>
              This cancels the appointment in Acuity — the client will receive Acuity's standard
              cancellation notice. Your reason below goes to the office only, never the client:
            </div>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Reason (shared with the office, not the client)"
              style={textareaStyle}
            />
            {error && <div style={errorStyle}>{error}</div>}
            <div style={actionRowStyle}>
              <button
                style={btn('var(--error-color)')}
                disabled={submitting || !note.trim()}
                onClick={() => decide('rejected', note.trim())}
              >
                {submitting ? 'Rejecting…' : 'Confirm reject'}
              </button>
              <button style={btnGhost} disabled={submitting} onClick={() => setView('main')}>Back</button>
            </div>
          </>
        )}

        {view === 'done' && (
          <>
            <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 8 }}>
              {doneDecision === 'accepted' ? '🎉 Accepted!' : doneDecision === 'rejected' ? 'Request rejected.' : 'Noted.'}
            </div>
            {doneDecision === 'accepted' && (
              <div style={{ marginBottom: 10 }}>
                {fullName} has been emailed a confirmation. Next step: create their client profile —
                you can send the intake link from there.
              </div>
            )}
            {doneDecision === 'rejected' && (
              <div style={{ marginBottom: 10 }}>
                The appointment was canceled in Acuity — {fullName} will receive the standard
                cancellation notice. Your reason went to the office only; any further outreach is
                their call.
              </div>
            )}
            {warnings.map((w, i) => (
              <div key={i} style={warningStyle}>⚠ {w}</div>
            ))}
            <div style={actionRowStyle}>
              {doneDecision === 'accepted' && (
                <button style={btn('var(--primary-color)')} onClick={() => { onCreateClient(prompt); onClose(); }}>
                  Create client profile →
                </button>
              )}
              <button style={btnGhost} onClick={onClose}>Close</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function labelize(key) {
  return key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());
}

const overlayStyle = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 };
const boxStyle = { background: 'var(--white)', borderRadius: 14, padding: 20, width: '100%', maxWidth: 560, maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 12px 40px rgba(0,0,0,0.3)' };
const headerStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 };
const closeBtnStyle = { background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: 'var(--text-muted)' };
const detailsBoxStyle = { background: 'var(--surface-muted)', border: '1px solid var(--border-color)', borderRadius: 10, padding: 12, marginBottom: 14, fontSize: 14, maxHeight: 260, overflowY: 'auto' };
const actionRowStyle = { display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 6 };
const radioRowStyle = { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, cursor: 'pointer' };
const textareaStyle = { width: '100%', minHeight: 80, borderRadius: 8, border: '1px solid var(--border-color)', padding: 10, fontSize: 14, marginBottom: 10, fontFamily: 'inherit' };
const errorStyle = { background: '#f8d7da', color: '#721c24', borderRadius: 8, padding: '8px 12px', marginBottom: 10, fontSize: 14 };
const warningStyle = { background: 'var(--surface-muted)', border: '1px solid var(--border-color)', borderRadius: 8, padding: '8px 12px', marginBottom: 8, fontSize: 13 };
const btn = (bg) => ({ background: bg, color: '#fff', border: 'none', borderRadius: 8, padding: '10px 16px', fontWeight: 700, fontSize: 14, cursor: 'pointer' });
const btnGhost = { background: 'transparent', color: 'var(--text-strong)', border: '1px solid var(--border-color)', borderRadius: 8, padding: '10px 16px', fontWeight: 600, fontSize: 14, cursor: 'pointer' };
